//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { ERoutesRoot } from '@/routes/RoutesEnum';
import { type AgentPage, type PageSnapshot, type UiAction } from './contract';
import {
  type ActionHandler, type PageSnapshotContextValue
} from './PageSnapshotContext';
import { handlerKey } from './usePageSnapshot';

export type DispatchResult = 'applied' | 'not_mounted';

// What the panel talks to.  Everything crossing it is a serializable
// message, so a later pop-out window adds a BroadcastChannel implementation
// without the panel changing.  See docs/design/agent-runtime.md,
// "Transport".
export interface AgentTransport {
  // The mounted page's publication, with the header's dataset added: the
  // one field no page publishes.  `null` when the header has none.
  readSnapshot(activeDataset: string | null): PageSnapshot;
  // Resolves once the action has been applied or found no page to apply it,
  // so a reply's actions can be dispatched in order: one that navigates
  // settles before the next is judged against the route.
  dispatch(action: UiAction): Promise<DispatchResult>;
}

const PAGE_ROUTES: Record<Exclude<AgentPage, 'other'>, string> = {
  graphs: ERoutesRoot.Graphs,
  dashboard: ERoutesRoot.Dashboard,
  datasets: ERoutesRoot.Datasets,
  explorer: ERoutesRoot.Explorer,
  jobs: ERoutesRoot.Jobs
};

// How long a dispatch waits for the current route's page to register the
// handler an action names.  Only a page still mounting is waited for: pages
// are lazy-loaded, so the route can be current for a moment before the
// component exists to register anything.
export const HANDLER_WAIT_MS = 5000;

export const routeForPage = (page: Exclude<AgentPage, 'other'>): string =>
  PAGE_ROUTES[page];

// The page id is derived from the route rather than published by each page,
// so a page that publishes nothing still identifies itself and cannot go
// stale.
export const pageForPath = (pathname: string): AgentPage => {
  const found = (Object.entries(PAGE_ROUTES) as
                 [Exclude<AgentPage, 'other'>, string][])
    .find(([, route]) => pathname === route ||
                         pathname.startsWith(`${route}/`));
  return found ? found[0] : 'other';
};

interface InProcessDeps {
  bridge: PageSnapshotContextValue;
  pathname: () => string;
  // Resolves once the router has moved, so the pathname reads the new route.
  navigate: (path: string) => Promise<void> | void;
}

// The handler, now or once the mounting page registers it, or undefined when
// the wait runs out.
const awaitHandler = (
  bridge: PageSnapshotContextValue, key: string
): Promise<ActionHandler | undefined> => new Promise((resolve) => {
  const found = bridge.getHandlers().get(key);
  if (found !== undefined) {
    resolve(found);
    return;
  }
  let unsubscribe = () => {};
  const timer = setTimeout(() => { unsubscribe(); resolve(undefined); },
                           HANDLER_WAIT_MS);
  unsubscribe = bridge.subscribe(() => {
    const handler = bridge.getHandlers().get(key);
    if (handler === undefined) return;
    clearTimeout(timer);
    unsubscribe();
    resolve(handler);
  });
});

export const createInProcessTransport = (
  { bridge, pathname, navigate }: InProcessDeps
): AgentTransport => ({
  readSnapshot(activeDataset) {
    const page = pageForPath(pathname());
    const published = bridge.getPublished();
    // A publication belongs to the page that made it; one left over from a
    // page the user has left is not what they are looking at.
    if (published !== null && published.page === page)
      return { ...published, active_dataset: activeDataset };
    if (page === 'graphs')
      return { active_dataset: activeDataset, page, sources: [],
               schema: null, load_results: null };
    return { active_dataset: activeDataset, page };
  },

  async dispatch(action) {
    if (action.kind === 'navigate') {
      await navigate(routeForPage(action.to));
      return 'applied';
    }
    // Only the mounted page may apply an action, and only if it said it
    // could.  Anything else is reported, never applied elsewhere.
    if (pageForPath(pathname()) !== action.page) return 'not_mounted';
    const handler =
      await awaitHandler(bridge, handlerKey(action.page, action.kind));
    if (handler === undefined) return 'not_mounted';
    handler(action);
    return 'applied';
  }
});
